import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import quiz from "./assets/content/quiz.json";
import { MultipleChoice } from "./components/MultipleChoice/MultipleChoice";
import { MultipleSelect } from "./components/MultipleSelect/MultipleSelect";
import { Ranking } from "./components/Ranking/Ranking";
import { Scale } from "./components/Scale/Scale";
import { Interstitial } from "./components/Interstitial/Interstitial";
import type { InterstitialStep } from "./components/Interstitial/Interstitial";
import { Button } from "./components/Button/Button";
import ProgressBar from "./components/ProgressBar/ProgressBar";
import { ResultsPage } from "./results-page";
import type {
  CareerKey,
  Question,
  MultipleChoiceQ,
  MultipleSelectQ,
  RankingQ,
  ScaleQ,
  ChoiceOption,
  Answers,
  Quiz,
  AuthoredLinks,
} from "./types/quiz";
import { trackComplete, trackCompleteStep } from "./utils/adobeAnalytics";
import { persistToApi } from "./utils/api";
import { useResizeObserver } from "@wojtekmaj/react-hooks";

type Step = { kind: "question"; q: Question } | InterstitialStep;

/** ---------- Scoring helpers ---------- */
function aggregateMultiSelect(
  selected: ChoiceOption[],
  careerKeys: CareerKey[],
  useAverage = true
) {
  const agg: Record<CareerKey, number> = Object.fromEntries(
    careerKeys.map((c) => [c, 0])
  ) as Record<CareerKey, number>;
  if (selected.length === 0) return agg;
  for (const o of selected)
    for (const c of careerKeys) agg[c] += o.weights[c] ?? 0;
  if (useAverage) for (const c of careerKeys) agg[c] /= selected.length;
  return agg;
}
function rankMultiplier(n: number, r: number) {
  return (n - r + 1) / ((n * (n + 1)) / 2);
}
function scoreQuiz(questions: Question[], answers: Answers) {
  const careerKeys = Object.keys(quiz.careers) as CareerKey[];
  const scores: Record<CareerKey, number> = Object.fromEntries(
    careerKeys.map((c) => [c, 0])
  ) as Record<CareerKey, number>;
  for (const q of questions) {
    const a = answers[q.id];
    if (!a) continue;
    const wQ = q.weight;

    if (q.type === "multiple_choice" && typeof a.chosenIndex === "number") {
      const opt = (q as MultipleChoiceQ).options[a.chosenIndex];
      for (const c of careerKeys) scores[c] += wQ * (opt?.weights[c] ?? 0);
    }

    if (q.type === "multiple_select" && Array.isArray(a.chosenIndices)) {
      const opts = (q as MultipleSelectQ).options;
      const selected = a.chosenIndices.map((i) => opts[i]).filter(Boolean);
      const agg = aggregateMultiSelect(selected, careerKeys, true);
      for (const c of careerKeys) scores[c] += wQ * agg[c];
    }

    if (q.type === "ranking" && Array.isArray(a.rankOrder)) {
      const opts = (q as RankingQ).options;
      const n = a.rankOrder.length;
      a.rankOrder.forEach((optIndex, pos) => {
        const m = rankMultiplier(n, pos + 1);
        const opt = opts[optIndex];
        for (const c of careerKeys)
          scores[c] += wQ * m * (opt?.weights[c] ?? 0);
      });
    }

    if (q.type === "scale" && typeof a.value === "number") {
      const sc = (q as ScaleQ).scale;
      const v = Math.min(sc.max, Math.max(sc.min, a.value));
      for (const c of careerKeys) {
        const arr = sc.weights[c];
        const idx = v - sc.min;
        scores[c] += wQ * (arr?.[idx] ?? 0);
      }
    }
  }
  return scores;
}
function normalizeScores(raw: Record<CareerKey, number>) {
  const vals = Object.values(raw);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (max === min)
    return Object.fromEntries(Object.keys(raw).map((k) => [k, 50])) as Record<
      CareerKey,
      number
    >;
  const out = {} as Record<CareerKey, number>;
  (Object.keys(raw) as CareerKey[]).forEach((k) => {
    out[k] = Math.round((100 * (raw[k] - min)) / (max - min));
  });
  return out;
}

/** ---------- Validation helper for step flow ---------- */
function isAnswerValid(q: Question, optionsViewed: boolean, a?: Answers[number]): boolean {
  if (!a) return false;
  switch (q.type) {
    case "multiple_choice":
      return typeof a.chosenIndex === "number" && optionsViewed;
    case "multiple_select": {
      const sel = a.chosenIndices ?? [];
      const mm = q as MultipleSelectQ;
      return sel.length >= mm.min_selections && sel.length <= mm.max_selections && optionsViewed;
    }
    case "ranking":
      return (
        Array.isArray(a.rankOrder) &&
        a.rankOrder.length === (q as RankingQ).options.length && optionsViewed
      );
    case "scale":
      return typeof a.value === "number" && optionsViewed;
    default:
      return false;
  }
}

/** ---------- Build initial answers ---------- */
function buildInitialAnswers(
  questions: Question[],
  initialRankingOrders: Record<number, number[]>
): Answers {
  const a: Answers = {};
  for (const q of questions) {
    if (q.type === "scale")
      a[q.id] = { value: Math.round((q.scale.min + q.scale.max) / 2) };
    if (q.type === "ranking")
      a[q.id] = { rankOrder: initialRankingOrders[q.id] };
  }
  return a;
}

function getIndustryPhraseFromJson(
  answers: Answers,
  questions: Question[],
  mcId = 10
): string | null {
  const q = questions.find(
    (qq) => qq.id === mcId && qq.type === "multiple_choice"
  ) as MultipleChoiceQ | undefined;
  if (!q) return null;
  const chosen = answers[q.id]?.chosenIndex;
  if (typeof chosen !== "number") return null;
  const opt = q.options[chosen];
  if (!opt) return null;
  const val = (opt as any).value as string | undefined;
  if (val === "broad_interests") return null;
  return opt.text;
}

function buildMarketingStatementFromJson(
  questions: Question[],
  answers: Answers,
  rankedCareers: Array<{ name: string }>
) {
  const industryPhrase = getIndustryPhraseFromJson(answers, questions);
  const yourTheme = rankedCareers?.[0]?.name ?? "your chosen path";

  const industry = industryPhrase ? (
    <>
      {" "}
      Add on top of that <span className="cq-font-bold">
        {industryPhrase}
      </span>{" "}
      are hungry for <span className="cq-font-bold">{yourTheme}</span>,
    </>
  ) : (
    <>
      {" "}
      Add on top of that the market is hungry for{" "}
      <span className="cq-font-bold">{yourTheme}</span>,
    </>
  );

  const close = (
    <>
      {" "}
      the scene may be perfectly set for your entry into{" "}
      <span className="cq-font-bold">{yourTheme}</span>.
    </>
  );

  // NOTE: no 'text-white' here so ResultsPage controls colors itself
  return (
    <p className="cq-text-lg cq-leading-relaxed">
      {industry}
      {close}
    </p>
  );
}

/** ---------- Background per step ---------- */
const interstitialBgById: Record<string, string> = {
  halfway: "cq-bg-black",
  "before-results": "cq-bg-black", // make the second interstitial page background black
};

function getBgClassForStep(current?: Step): string {
  const defaultQuestionBg = "cq-bg-cfa-bright-blue-30";
  const defaultInterstitialBg = "cq-bg-black";
  if (!current) return defaultQuestionBg;
  if (current.kind === "interstitial") {
    return interstitialBgById[current.id] ?? defaultInterstitialBg;
  }
  return defaultQuestionBg;
}

function getBgClassForFooter(current?: Step): string {
  const defaultQuestionBg = "cq-bg-cfa-bright-blue-30/70";
  const defaultInterstitialBg = "cq-bg-black/50";
  if (!current) return defaultQuestionBg;
  if (current.kind === "interstitial") {
    return interstitialBgById[current.id] ?? defaultInterstitialBg;
  }
  return defaultQuestionBg;
}

/** ---------- Main component ---------- */
function CareerCompass({ authoredLinks, setStarted }: { authoredLinks: AuthoredLinks, setStarted: (v: boolean) => void }) {
  const questions = useMemo(
    () => (quiz.questions as Question[]).slice().sort((a, b) => a.id - b.id),
    []
  );

  const initialRankingOrders = useMemo(() => {
    const map: Record<number, number[]> = {};
    for (const q of questions)
      if (q.type === "ranking") map[q.id] = q.options.map((_, i) => i);
    return map;
  }, [questions]);

  const [answers, setAnswers] = useState<Answers>(() =>
    buildInitialAnswers(questions, initialRankingOrders)
  );
  function setAnswer(id: number, patch: Partial<Answers[number]>) {
    setAnswers((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  const steps = useMemo<Step[]>(() => {
    const base: Step[] = questions.map((q) => ({ kind: "question", q }));

    const totalQs = questions.length;
    if (totalQs > 0) {
      const halfwayIndex = Math.floor(totalQs / 2);
      const remaining = totalQs - halfwayIndex;
      const remainingLabel = remaining === 1 ? "question" : "questions";

      const interstitialHalfway: InterstitialStep = {
        kind: "interstitial",
        id: "halfway",

        title: `Looking good!`,
        subtitle: `Just ${remaining} more ${remainingLabel} to go...`,
        body: "Your personalized career path is taking shape. Keep going to see the final look.",
        layout: "",
        imageSrc: "icons/halfway.svg",
        imageAlt: "Looking Good!",
      };
      base.splice(halfwayIndex, 0, interstitialHalfway);
    }

    const interstitialBeforeResults: InterstitialStep = {
      kind: "interstitial",
      id: "before-results",
      subtitle: "",
      layout: "split",
      title: "Finding your fit...",
      imageSrc: "generating-report-desktop.gif",
    };
    base.push(interstitialBeforeResults);

    return base;
  }, [questions]);


  const [questionBottomViewed, setQuestionBottomViewed] = useState<boolean>(false);
  const [questionIsScrolled, setQuestionIsScrolled] = useState<boolean>(false);

  const handleQuestionIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      setQuestionIsScrolled(entry.isIntersecting);
      if (entry.isIntersecting) {
        setQuestionBottomViewed(true);
      }
    });
  }
  
  const footerContainer = useRef<HTMLDivElement>(null);
  const footerHeight = useRef<any>(footerContainer.current?.getBoundingClientRect().height);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const questionObserver = useRef<IntersectionObserver|null>();

  useEffect(() => {
      if (footerContainer.current) {
        footerHeight.current = Math.round(footerContainer.current.getBoundingClientRect().height);
      }
  }, [footerContainer]);

  useResizeObserver(document.body, {}, () => {
    const height = Math.round(footerContainer.current?.getBoundingClientRect().height as any) || 0;
    if (height !== footerHeight.current || !questionObserver.current) {
      // Init intersection observer on resize
      if (questionObserver.current) {
        questionObserver.current.disconnect();
      }
      
      footerHeight.current = height;
      
      questionObserver.current = new IntersectionObserver(
        handleQuestionIntersection, {
          rootMargin: `0px 0px -${height}px 0px`
        } as any
      );
    }
  });

  const [step, setStep] = useState(0);
  const timer = useRef(new Date());
  const [finished, setFinished] = useState(false);
  const total = steps.length;
  const current = steps[step];
  const currentQ = current?.kind === "question" ? current.q : undefined;
  const currentA = currentQ ? answers[currentQ.id] : undefined;
  const canNext =
    current?.kind === "interstitial"
      ? true
      : currentQ
      ? isAnswerValid(currentQ, questionBottomViewed, currentA)
      : false;
  
  // Reset view timer on step change
  useEffect(() => {
    timer.current = new Date();
  }, [step]);
  
  // Setup intersection observer to detect if options have been viewed
  useEffect(() => {
    // Reset quesetion viewed on step change
    setQuestionBottomViewed(false);
    setTimeout(() => {
      if (scrollTargetRef.current && questionObserver.current) {
        questionObserver.current.observe(scrollTargetRef.current);

        return () => {
          if (questionObserver.current) {
            questionObserver.current.disconnect();
          }
        }
      }
    }, 700) // wait for animation in to finish
  }, [step]);

  /* Countdown timer*/
  const AUTO_ADVANCE_MS = 5000;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Enter" && canNext && !finished) {
        if (step < total - 1) setStep(step + 1);
        else setFinished(true);
      }
      if (e.altKey && e.key === "ArrowLeft") setStep((s) => Math.max(0, s - 1));
      if (e.altKey && e.key === "ArrowRight" && canNext)
        setStep((s) => Math.min(total - 1, s + 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, total, canNext, finished]);

  // combined interstitial countdown + auto-advance
  useEffect(() => {
    if (finished) return;

    // Only auto-advance interstitials that are NOT the first one (id: "halfway")
    if (current?.kind !== "interstitial" || current.id === "halfway") {
      return;
    }

    const started = Date.now();

    const tick = window.setInterval(() => {
      const elapsed = Date.now() - started;
      const secsLeft = Math.max(
        0,
        Math.ceil((AUTO_ADVANCE_MS - elapsed) / 1000)
      );

      if (secsLeft <= 0) {
        window.clearInterval(tick);
        const isLast = step >= total - 1;
        if (isLast) {
          setFinished(true);
        } else {
          setStep((s) => Math.min(total - 1, s + 1));
        }
      }
    }, 250);

    return () => window.clearInterval(tick);
  }, [current, step, total, finished]);

  const rawScores = useMemo(
    () => scoreQuiz(questions, answers),
    [questions, answers]
  );
  const pctScores = useMemo(() => normalizeScores(rawScores), [rawScores]);

  const rankedCareers = useMemo(
    () =>
      (Object.keys(quiz.careers) as CareerKey[])
        .map((key) => ({
          key,
          name: quiz.careers[key].name,
          description: quiz.careers[key].whyThisFits,
          image: quiz.careers[key].image,
          otherImage : quiz.careers[key].otherImage,
          pct: pctScores[key],
        }))
        .sort((a, b) => b.pct - a.pct),
    [pctScores]
  );

  const marketingNode = useMemo(
    () => buildMarketingStatementFromJson(questions, answers, rankedCareers),
    [questions, answers, rankedCareers]
  );

  function resetQuiz() {
    setAnswers(buildInitialAnswers(questions, initialRankingOrders));
    setStep(0);
    setFinished(false);
    backToTop();
    setTimeout(()=> {
      window.scrollTo(0,0);
    },250)
  }

  function backToTop(){
    if(scrollContainer.current){
      scrollContainer.current.scrollIntoView({behavior: "instant", inline: "start"});
    }
  }

  /** ---------- EARLY RETURN: standalone Results page ---------- */
  if (finished) {
    return (
      <ResultsPage
        authoredLinks={authoredLinks}
        rankedCareers={rankedCareers}
        pctScores={pctScores}
        marketingNode={marketingNode}
        onRestart={resetQuiz}
      />
    );
  }

  const bgClass = getBgClassForStep(current);
  const footerBgClass = getBgClassForFooter(current);

  // remove the top padding when showing any interstitial so the image starts
  // under the header (no colored bar at the top)
  const topPadClass =
    current?.kind === "interstitial" ? "cq-pt-11" : "cq-pt-11";

  return (
    <div
      className={`cq-relative cq-flex cq-flex-col cq-h-full ${bgClass} ${topPadClass}`}
    >
      {/* Main content */}
      {current?.kind === "question" ? (
        // ---------- QUESTION: fixed width ----------
        <div ref={scrollContainer} className="cq-mx-auto cq-px-4 cq-min-h-auto cq-max-h-auto cq-scroll-my-[7rem] cq-flex cq-flex-col cq-w-full cq-max-w-[1190px]">
          {/* Progress */}
          <section className="cq-py-8 md:cq-py-10">
            <div className="cq-flex cq-flex-col cq-gap-2">
              <ProgressBar progress={((step + 1) / total) * 100} />
            </div>
          </section>

          {/* Step content */}
          <section className="cq-flex cq-flex-col cq-gap-8">
            <div
              key={`q-${currentQ!.id}`}
              className="cq-flex cq-flex-col cq-animate-fadeTop"
            >
              <div className="cq-flex cq-flex-col cq-items-center">
                <h1
                  className={`cq-max-w-4xl cq-text-balance cq-text-center cq-text-3xl md:cq-text-5xl lg:cq-text-6xl cq-leading-tight cq-text-black cq-font-medium ${currentQ!.type === 'scale' ? 'cq-mb-6 md:cq-mb-0' : ''}`}
                  dangerouslySetInnerHTML={{ __html: currentQ!.question }}
                />
                {currentQ!.description && (
                  <p className={`cq-text-center cq-text-base cq-text-black cq-py-6 md:cq-py-8 cq-mb-0 ${currentQ!.type === 'scale' ? 'cq-hidden md:cq-inline' : ''}`}>
                    {currentQ!.description}
                  </p>
                )}
              </div>

              {currentQ!.type === "multiple_choice" && (
                <MultipleChoice
                  q={currentQ as MultipleChoiceQ}
                  value={answers[currentQ!.id]?.chosenIndex}
                  onChange={(i) => setAnswer(currentQ!.id, { chosenIndex: i })}
                />
              )}

              {currentQ!.type === "multiple_select" && (
                <MultipleSelect
                  q={currentQ as MultipleSelectQ}
                  values={new Set(answers[currentQ!.id]?.chosenIndices ?? [])}
                  onToggle={(i) => {
                    const prev = new Set(
                      answers[currentQ!.id]?.chosenIndices ?? []
                    );
                    if (prev.has(i)) prev.delete(i);
                    else prev.add(i);
                    const mm = currentQ as MultipleSelectQ;
                    const arr = Array.from(prev).slice(0, mm.max_selections);
                    setAnswer(currentQ!.id, { chosenIndices: arr });
                  }}
                />
              )}

              {currentQ!.type === "ranking" && (
                <Ranking
                  q={currentQ as RankingQ}
                  order={
                    answers[currentQ!.id]?.rankOrder ??
                    currentQ!.options.map((_, i) => i)
                  }
                  onChange={(next) =>
                    setAnswer(currentQ!.id, { rankOrder: next })
                  }
                />
              )}

              {currentQ!.type === "scale" && (
                <Scale
                  q={currentQ as ScaleQ}
                  value={
                    answers[currentQ!.id]?.value ??
                    Math.round(
                      ((currentQ as ScaleQ).scale.min +
                        (currentQ as ScaleQ).scale.max) /
                        2
                    )
                  }
                  onChange={(v) => setAnswer(currentQ!.id, { value: v })}
                />
              )}
              {/* Scroll target */}
              <div ref={scrollTargetRef} className="cq-mb-1" />
            </div>
          </section>
        </div>
      ) : (
        // ---------- INTERSTITIAL: full-bleed (no max width)
        <div
          ref={scrollContainer} key={`interstitial-${(current as InterstitialStep).id}`}
          className="cq-mx-auto cq-my-auto cq-px-4 cq-flex cq-flex-col cq-justify-center cq-w-full cq-scroll-my-[7rem] cq-max-w-[1190px]"
        >
          <Interstitial step={current as InterstitialStep} />
        </div>
      )}

      {/* Footer Nav */}
      {current.kind === "interstitial" && current?.id === "before-results" ? (
        <></>
      ) : (
        <footer
          className={`cq-mx-auto cq-sticky cq-mt-auto cq-bottom-0 cq-left-0 cq-w-full cq-z-5 ${footerBgClass}`}
          ref={footerContainer}
          >
          <div className="cq-mx-auto cq-flex cq-items-center cq-justify-between cq-px-4 cq-py-6 md:cq-pb-11 cq-max-w-[1190px]">
            <Button
              variant="secondary"
              onDark={current?.kind === "interstitial"}
              arrow="left"
              onClick={() => {
                backToTop();
                if (step === 0) {
                  setStarted(false);
                }
                setStep((s) => {
                  const prev = steps[s - 1];
                  const delta = (prev?.kind === "interstitial" ? 2 : 1);
                  return Math.max(0, s - delta)
                });
              }}
            >
              Back
            </Button>

            {/* Scroll indicator */}
            <div className={`cq-transition-opacity cq-pointer-events-none motion-safe:cq-animate-scrollBounce blur cq-bg-transparent cq-p-2 cq-rounded-full cq-text-black cq-border-dashed cq-border-black cq-border cq-flex cq-align-center cq-justify-center cq-w-8 cq-h-8 cq-bottom-1/2 cq-end-1/2 ${questionIsScrolled ? 'cq-opacity-0' : 'cq-opacity-30'}`} aria-hidden={questionIsScrolled ? 'true' : 'false'}>
              <span className="cq-font-material material-symbols-outlined cq-text-base cq-leading-none">arrow_cool_down</span>
            </div>

            {step < total - 2 ? (
              <Button
                onClick={() => {
                  backToTop();
                  if (current?.kind !== "interstitial") {
                    const timeSpent: number = Math.round(
                      (
                        (
                          (new Date().getTime()) - timer.current?.getTime()
                        ) / 1000
                      )
                    );
                    backToTop();
                    trackCompleteStep(
                      currentQ?.id,
                      questions,
                      answers[currentQ?.id ?? 0],
                      timeSpent,
                    );
                  }
                  setStep((s) => Math.min(total - 1, s + 1));
                }}
                disabled={!canNext}
              >
                Continue
              </Button>
            ) : (
              <Button
                onClick={() => {
                  backToTop();
                  const timeSpent: number = Math.round(
                    (
                      (
                        (new Date().getTime()) - timer.current?.getTime()
                      ) / 1000
                    )
                  );
                  trackCompleteStep(
                    currentQ?.id,
                    questions,
                    answers[currentQ?.id ?? 0],
                    timeSpent,
                  );
                  trackComplete(rankedCareers);
                  persistToApi(quiz as Quiz);
                  setFinished(true);
                }}
                disabled={!canNext}
              >
                See results
              </Button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

export default CareerCompass;
export { CareerCompass };
